import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import PersonIcon from "@mui/icons-material/Person";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useTranslation } from "react-i18next";
import { Auth } from "aws-amplify";
import { getPlayerNickname, savePlayerNickname } from "../utils/playerState";
import { useTheme as useAppTheme, ThemeName } from "../contexts/ThemeContext";

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { themeName, setThemeName } = useAppTheme();
  const [nickname, setNickname] = useState("");
  const [localTheme, setLocalTheme] = useState<ThemeName>(themeName);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        setIsLoggedIn(true);

        const savedNickname = await getPlayerNickname();
        if (savedNickname) {
          setNickname(savedNickname);
        }
      } catch {
        setIsLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Sync theme when component mounts
  useEffect(() => {
    setLocalTheme(themeName);
  }, [themeName]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(false);

    try {
      // Save theme (always works, even for anonymous users)
      setThemeName(localTheme);

      // Save nickname (only for logged-in users)
      if (isLoggedIn && nickname.trim()) {
        const success = await savePlayerNickname(nickname.trim());
        if (!success) {
          setSaveError(true);
          setIsSaving(false);
          return;
        }
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t("Settings.title")}
      </Typography>

      {/* Theme Settings */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PaletteIcon />
            {t("PersonalSettings.themeSettings")}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="theme-label">
              {t("PersonalSettings.selectTheme")}
            </InputLabel>
            <Select
              labelId="theme-label"
              value={localTheme}
              onChange={(e) => setLocalTheme(e.target.value as ThemeName)}
              label={t("PersonalSettings.selectTheme")}
              disabled={isSaving}
            >
              <MenuItem value="classic">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoFixHighIcon fontSize="small" />
                  {t("PersonalSettings.themeClassic")}
                </Box>
              </MenuItem>
              <MenuItem value="modern">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LightModeIcon fontSize="small" />
                  {t("PersonalSettings.themeModern")}
                </Box>
              </MenuItem>
              <MenuItem value="dark">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DarkModeIcon fontSize="small" />
                  {t("PersonalSettings.themeDark")}
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText>{t("PersonalSettings.themeHelper")}</FormHelperText>
          </FormControl>

          {/* Theme Preview */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2" color="text.primary">
              {localTheme === "classic" &&
                t("PersonalSettings.themeClassicDescription")}
              {localTheme === "modern" &&
                t("PersonalSettings.themeModernDescription")}
              {localTheme === "dark" &&
                t("PersonalSettings.themeDarkDescription")}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Profile Settings (Nickname) */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PersonIcon />
            {t("Settings.profile")}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Settings.nicknameDescription")}
          </Typography>

          <TextField
            label={t("Settings.nickname")}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={isSaving || !isLoggedIn}
            helperText={!isLoggedIn ? t("Settings.loginRequired") : undefined}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving}
          fullWidth
          size="large"
        >
          {isSaving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("Settings.save")
          )}
        </Button>

        {saveSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t("Settings.saveSuccess")}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("Settings.saveError")}
          </Alert>
        )}
      </Box>
    </Box>
  );
};
